"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import type { CurrentUserModel } from "@/api";

import { getOrgPersonByUserId } from "@/actions/people";
import { getUserFullNameByEmail } from "@/actions/users";
import { queryKeys } from "@/constants/query";
import { useLoggedUser } from "@/hooks/use-logged-user";
import { isValidOrgId } from "@/lib/utils";

function formatPersonDisplayName(person: {
  firstName?: string | null;
  lastName?: string | null;
} | null | undefined): string {
  if (!person) {
    return "";
  }
  const parts = [person.firstName, person.lastName].filter(
    (p): p is string => typeof p === "string" && p.trim().length > 0,
  );
  return parts.join(" ").trim();
}

function getMenuInitials(displayName: string): string {
  const base = displayName.trim();
  if (!base) {
    return "";
  }
  const parts = base.split(/\s+/);
  const first = (parts[0]?.[0] ?? "").toUpperCase();
  const second =
    parts.length > 1 && parts[1]?.[0] ? parts[1][0].toUpperCase() : "";
  return first + second;
}

/**
 * Canonical label + initials for user menu / sidebar footer.
 * Prefers org Person (My Profile) over `/me` and JWT `session.user.name` so names stay in sync.
 */
export function useProfileMenuDisplayName(): {
  displayName: string;
  initials: string;
  email: string;
  orgId: string | null;
  user: CurrentUserModel | null | undefined;
  isLoading: boolean;
} {
  const { user, orgId, isLoading } = useLoggedUser();

  const email = user?.email ?? "";
  const userId =
    typeof user?.id === "string" && user.id.length > 0 ? user.id : "";
  const orgIdForPerson =
    typeof orgId === "string" && isValidOrgId(orgId) ? orgId : null;
  const orgPersonScope = !!orgIdForPerson && userId.length > 0;

  const {
    data: orgPerson,
    isPending: orgPersonPending,
    isError: orgPersonError,
    isFetched: orgPersonFetched,
  } = useQuery({
    queryKey: queryKeys.getOrgPersonByUserId(orgIdForPerson ?? "", userId),
    queryFn: () => getOrgPersonByUserId(orgIdForPerson ?? "", userId),
    enabled: orgPersonScope,
  });

  const orgPersonName = formatPersonDisplayName(orgPerson);

  const meName =
    user?.name && user.name !== "null"
      ? user.name.replace(" null", "")
      : "";

  const shouldFetchPrisma =
    !!email &&
    (!user?.name ||
      user.name === "null" ||
      (orgPersonScope && orgPersonFetched && !orgPersonName));

  const { data: prismaFullName } = useQuery({
    queryKey: ["userFullNameByEmail", email],
    queryFn: () => getUserFullNameByEmail(email),
    enabled: shouldFetchPrisma,
  });

  const emailLocal = email ? email.split("@")[0] : "";

  const displayName = useMemo((): string => {
    if (orgPersonName.length > 0) {
      return orgPersonName;
    }
    if (orgPersonScope && orgPersonPending && !orgPersonError) {
      return emailLocal || "User";
    }
    if (meName.length > 0) {
      return meName;
    }
    if (typeof prismaFullName === "string" && prismaFullName.length > 0) {
      return prismaFullName;
    }
    return emailLocal || "User";
  }, [
    emailLocal,
    meName,
    orgPersonError,
    orgPersonName,
    orgPersonPending,
    orgPersonScope,
    prismaFullName,
  ]);

  const initials = useMemo(
    () => getMenuInitials(displayName),
    [displayName],
  );

  return {
    displayName,
    initials,
    email,
    orgId: orgId ?? null,
    user,
    isLoading,
  };
}
